import { CommonModule } from '@angular/common'
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core'
import { NgxTippyModule } from 'ngx-tippy-wrapper'

@Component({
  selector: 'app-persona-switcher',
  templateUrl: './persona-switcher.component.html',
  styleUrls: ['./persona-switcher.component.scss'],
  standalone: true,
  imports: [CommonModule, NgxTippyModule]
})
export class PersonaSwitcherComponent implements AfterViewInit, OnDestroy, OnChanges {
  @Input() personas: Array<{ title: string; count?: number; active?: boolean; childCount?: number; originalIndex?: number }> = []
  @Input() selectedIndex: number = 0
  @Input() disabled: boolean = false

  @Output() personaChanged = new EventEmitter<number>()
  @Output() scrollLeft = new EventEmitter<void>()
  @Output() scrollRight = new EventEmitter<void>()

  @ViewChild('personasList', { static: false }) personasListRef: ElementRef<HTMLElement>

  // Cached scroll state to avoid delay in calculations
  public canScrollLeft = false
  public canScrollRight = false
  private _scrollListener: ((event: Event) => void) | null = null

  constructor(private cdr: ChangeDetectorRef) {}

  /**
   * Gets the personas list element using ViewChild
   */
  private get personasListElement(): HTMLElement | null {
    return this.personasListRef?.nativeElement || null
  }

  onPersonaClick(index: number) {
    // Find the clicked persona
    const clickedPersona = this.personas[index]
    if (!clickedPersona) return

    // Emit the filtered index if not disabled and not already active
    if (!this.disabled && !clickedPersona.active) {
      this.personaChanged.emit(index)
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Only recalculate scroll state when personas array actually changes (not just reference)
    if (changes['personas'] && !changes['personas'].firstChange) {
      const prevPersonas = changes['personas'].previousValue
      const currPersonas = changes['personas'].currentValue

      // Only update if the personas array length or content actually changed
      if (
        !prevPersonas ||
        !currPersonas ||
        prevPersonas.length !== currPersonas.length ||
        prevPersonas.some((p: any, i: number) => !currPersonas[i] || p.originalIndex !== currPersonas[i].originalIndex)
      ) {
        setTimeout(() => {
          this._updateScrollState()
        }, 0)
      }
    }
  }

  ngAfterViewInit(): void {
    // Use setTimeout with a small delay to ensure the element is rendered
    // Retry mechanism in case the element isn't ready yet
    const trySetup = (attempts = 0) => {
      if (this.personasListElement) {
        this._setupScrollListener()
        this._updateScrollState()
      } else if (attempts < 10) {
        // Retry up to 10 times (100ms total wait)
        setTimeout(() => trySetup(attempts + 1), 10)
      }
    }
    trySetup()
  }

  /**
   * Cleans up scroll listener on component destruction
   */
  ngOnDestroy(): void {
    if (this._scrollListener && this.personasListElement) {
      this.personasListElement.removeEventListener('scroll', this._scrollListener)
    }
  }

  /**
   * Sets up scroll event listener to track scroll state in real-time
   * Only responds to scrolls on the personas list element
   */
  private _setupScrollListener(): void {
    if (!this.personasListElement) return

    // Remove existing listener if any
    if (this._scrollListener) {
      this.personasListElement.removeEventListener('scroll', this._scrollListener)
    }

    this._scrollListener = (event: Event) => {
      // Only update if the scroll event is from the personas list element
      if (event.target != this.personasListElement) {
        return
      }
      this._updateScrollState()
    }

    this.personasListElement.addEventListener('scroll', this._scrollListener, { passive: true })
  }

  /**
   * Updates the cached scroll state based on current scroll position
   */
  private _updateScrollState(): void {
    this.canScrollLeft = this.personasListElement.scrollLeft > 0
    this.canScrollRight = this.personasListElement.scrollLeft + this.personasListElement.clientWidth < this.personasListElement.scrollWidth - 10
    // Trigger change detection to update the template
    this.cdr.detectChanges()
  }
  public onScrollLeft() {
    this.personasListElement.scrollLeft = this.personasListElement.scrollLeft - this.personasListElement.clientWidth

    requestAnimationFrame(() => {
      this._updateScrollState()
    })
  }

  public onScrollRight() {
    this.personasListElement.scrollLeft = this.personasListElement.scrollLeft + this.personasListElement.clientWidth

    requestAnimationFrame(() => {
      this._updateScrollState()
    })
  }

  /**
   * Finds the current persona's position in the filtered personas array
   */
  private _getCurrentPersonaIndex(): number {
    return this.personas.findIndex((p) => p.active === true)
  }

  getVisiblePersonas(): Array<{ title: string; count?: number; active?: boolean; childCount?: number; originalIndex?: number; index: number }> {
    return this.personas.map((persona, index) => ({
      ...persona,
      index,
      active: persona.active
    }))
  }

  trackByIndex(index: number, item: any): number {
    return item.index
  }
}
